import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './PhotoViewer.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function PhotoViewer({ photo, onClose, onAlbumChange, onDelete }) {
    const [photoDetails, setPhotoDetails] = useState(null);
    const [albums, setAlbums] = useState([]);
    const [selectedAlbumId, setSelectedAlbumId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');
    const [isPublished, setIsPublished] = useState(false);
    const [publishSaving, setPublishSaving] = useState(false);
    const [title, setTitle] = useState('');
    const [titleSaving, setTitleSaving] = useState(false);
    const [isCover, setIsCover] = useState(false);
    const [coverSaving, setCoverSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchPhotoDetails();
        fetchAlbums();
    }, [photo.id]);

    const fetchPhotoDetails = async () => {
        try {
            const response = await axios.get(`${API_BASE}/photos/${photo.id}`, {
                withCredentials: true
            });
            setPhotoDetails(response.data);
            setSelectedAlbumId(response.data.album_id);
            setIsPublished(response.data.published || false);
            setTitle(response.data.title || '');
            setIsCover(response.data.is_cover || false);
        } catch (error) {
            console.error('Error fetching photo details:', error);
        }
    };

    const fetchAlbums = async () => {
        try {
            const response = await axios.get(`${API_BASE}/albums`, {
                withCredentials: true
            });
            setAlbums(response.data);
        } catch (error) {
            console.error('Error fetching albums:', error);
        }
    };

    const handleAlbumChange = async (e) => {
        const newAlbumId = parseInt(e.target.value);
        setSelectedAlbumId(newAlbumId);
        setSaving(true);
        setSaveMessage('');

        try {
            await axios.put(`${API_BASE}/photos/${photo.id}/album`, {
                albumId: newAlbumId
            }, {
                withCredentials: true
            });

            setSaveMessage('Album updated successfully!');
            setTimeout(() => setSaveMessage(''), 3000);

            // Update the photo details
            setPhotoDetails(prev => ({
                ...prev,
                album_id: newAlbumId,
                album_name: albums.find(a => a.id === newAlbumId)?.name
            }));

            // Notify parent component if callback provided
            if (onAlbumChange) {
                onAlbumChange();
            }
        } catch (error) {
            console.error('Error updating album:', error);
            setSaveMessage('Failed to update album');
            // Revert selection on error
            setSelectedAlbumId(photoDetails?.album_id);
        } finally {
            setSaving(false);
        }
    };

    const handlePublishedChange = async (e) => {
        const newPublishedStatus = e.target.checked;
        setIsPublished(newPublishedStatus);
        setPublishSaving(true);

        try {
            await axios.put(`${API_BASE}/photos/${photo.id}/published`, {
                published: newPublishedStatus
            }, {
                withCredentials: true
            });

            // Update the photo details
            setPhotoDetails(prev => ({
                ...prev,
                published: newPublishedStatus
            }));
        } catch (error) {
            console.error('Error updating published status:', error);
            // Revert on error
            setIsPublished(!newPublishedStatus);
        } finally {
            setPublishSaving(false);
        }
    };

    const handleTitleBlur = async () => {
        // Only save if title has changed
        if (title === (photoDetails?.title || '')) {
            return;
        }

        setTitleSaving(true);

        try {
            await axios.put(`${API_BASE}/photos/${photo.id}/title`, {
                title: title
            }, {
                withCredentials: true
            });

            // Update the photo details
            setPhotoDetails(prev => ({
                ...prev,
                title: title
            }));
        } catch (error) {
            console.error('Error updating title:', error);
            // Revert on error
            setTitle(photoDetails?.title || '');
        } finally {
            setTitleSaving(false);
        }
    };

    const handleCoverChange = async (e) => {
        const newCoverStatus = e.target.checked;
        setIsCover(newCoverStatus);
        setCoverSaving(true);

        try {
            await axios.put(`${API_BASE}/photos/${photo.id}/cover`, {
                isCover: newCoverStatus
            }, {
                withCredentials: true
            });

            // Update the photo details
            setPhotoDetails(prev => ({
                ...prev,
                is_cover: newCoverStatus
            }));
        } catch (error) {
            console.error('Error updating cover status:', error);
            // Revert on error
            setIsCover(!newCoverStatus);
        } finally {
            setCoverSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);

        try {
            await axios.delete(`${API_BASE}/photos/${photo.id}`, {
                withCredentials: true
            });

            // Notify parent component
            if (onDelete) {
                onDelete(photo.id);
            }
            onClose();
        } catch (error) {
            console.error('Error deleting photo:', error);
            alert('Failed to delete photo. Please try again.');
            setShowDeleteConfirm(false);
        } finally {
            setDeleting(false);
        }
    };

    // Format field names to be more readable
    const formatFieldName = (key) => {
        // Special cases for better naming
        const nameMap = {
            'ISO': 'ISO',
            'GPSLatitude': 'GPS Latitude',
            'GPSLongitude': 'GPS Longitude',
            'GPSAltitude': 'GPS Altitude',
            'FNumber': 'Aperture (f-number)',
            'ExposureTime': 'Shutter Speed',
            'FocalLength': 'Focal Length',
            'FocalLengthIn35mmFormat': 'Focal Length (35mm)',
            'LensModel': 'Lens Model',
            'LensMake': 'Lens Make',
            'DateTimeOriginal': 'Date/Time Original',
            'CreateDate': 'Create Date',
            'ModifyDate': 'Modify Date',
            'ImageWidth': 'Image Width',
            'ImageHeight': 'Image Height',
            'ExifImageWidth': 'EXIF Image Width',
            'ExifImageHeight': 'EXIF Image Height',
            'BitsPerSample': 'Bits Per Sample',
            'ColorSpace': 'Color Space',
            'WhiteBalance': 'White Balance',
            'Flash': 'Flash',
            'ExposureMode': 'Exposure Mode',
            'ExposureProgram': 'Exposure Program',
            'MeteringMode': 'Metering Mode',
            'LightSource': 'Light Source',
            'Saturation': 'Saturation',
            'Sharpness': 'Sharpness',
            'Contrast': 'Contrast',
            'SceneCaptureType': 'Scene Capture Type',
            'GainControl': 'Gain Control',
            'SubjectDistanceRange': 'Subject Distance Range',
            'DigitalZoomRatio': 'Digital Zoom Ratio'
        };

        if (nameMap[key]) return nameMap[key];

        // Convert camelCase or PascalCase to Title Case with spaces
        return key
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    };

    // Format values for special display
    const formatMetadataValue = (key, value) => {
        if (value === null || value === undefined) return 'N/A';

        // Handle special formatting cases
        if (key === 'FNumber' && typeof value === 'number') {
            return `f/${value}`;
        }
        if (key === 'ExposureTime' && typeof value === 'number') {
            if (value < 1) {
                return `1/${Math.round(1/value)}s`;
            }
            return `${value}s`;
        }
        if (key === 'FocalLength' && typeof value === 'number') {
            return `${value}mm`;
        }
        if (key === 'FocalLengthIn35mmFormat' && typeof value === 'number') {
            return `${value}mm`;
        }
        if ((key === 'ImageWidth' || key === 'ExifImageWidth') && typeof value === 'number') {
            const heightKey = key.replace('Width', 'Height');
            const height = photoDetails?.metadata?.[heightKey];
            if (height) {
                return `${value} x ${height} pixels`;
            }
            return `${value} pixels`;
        }
        if (key === 'ImageHeight' || key === 'ExifImageHeight') {
            // Skip height if we already showed it with width
            return null;
        }
        if ((key === 'GPSLatitude' || key === 'GPSLongitude') && typeof value === 'number') {
            return value.toFixed(6);
        }
        if (key === 'latitude' || key === 'longitude') {
            return value.toFixed(6);
        }
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        if (typeof value === 'object' && !Array.isArray(value)) {
            // For nested objects, show as JSON
            return JSON.stringify(value, null, 2);
        }
        if (Array.isArray(value)) {
            return value.join(', ');
        }
        if (value instanceof Date) {
            return value.toLocaleString();
        }

        return String(value);
    };

    // Organize metadata into categories - show only essential fields
    const categorizeMetadata = (metadata) => {
        // Define which fields to show (basic subset)
        const allowedFields = {
            'Camera': ['Make', 'Model'],
            'Lens': ['LensModel', 'LensMake'],
            'Exposure': ['ISO', 'FNumber', 'ExposureTime', 'FocalLength', 'FocalLengthIn35mmFormat'],
            'Image': ['ImageWidth', 'ExifImageWidth'],
            'Date & Time': ['DateTimeOriginal', 'CreateDate']
        };

        const categories = {
            'Camera': [],
            'Lens': [],
            'Exposure': [],
            'Image': [],
            'Date & Time': []
        };

        Object.entries(metadata).forEach(([key, value]) => {
            // Check if this field is in our allowed list
            for (const [category, fields] of Object.entries(allowedFields)) {
                if (fields.includes(key)) {
                    const formattedValue = formatMetadataValue(key, value);
                    if (formattedValue === null) return; // Skip

                    const item = { key, label: formatFieldName(key), value: formattedValue };
                    categories[category].push(item);
                    break;
                }
            }
        });

        // Remove empty categories
        return Object.fromEntries(
            Object.entries(categories).filter(([_, items]) => items.length > 0)
        );
    };

    const metadata = photoDetails?.metadata || {};
    const categorizedMetadata = categorizeMetadata(metadata);

    return (
        <div className="photo-viewer-overlay" onClick={onClose}>
            <div className="photo-viewer-container" onClick={(e) => e.stopPropagation()}>
                <button className="close-btn" onClick={onClose}>&times;</button>

                <div className="photo-viewer-content">
                    <div className="photo-viewer-image">
                        <img
                            src={`${API_BASE}/photos/${photo.id}/file`}
                            alt={photo.original_filename}
                        />
                    </div>

                    <div className="photo-viewer-metadata">
                        <h2>Photo Details</h2>

                        <table className="metadata-table">
                            <tbody>
                                <tr>
                                    <th>Filename:</th>
                                    <td>{photo.original_filename}</td>
                                </tr>
                                <tr>
                                    <th>Uploaded by:</th>
                                    <td>{photo.username}</td>
                                </tr>
                                <tr>
                                    <th>Upload date:</th>
                                    <td>{new Date(photo.uploaded_at).toLocaleString()}</td>
                                </tr>
                                {photoDetails && (
                                    <>
                                        <tr>
                                            <th>Views:</th>
                                            <td className="stats-value">
                                                <span className="stats-icon">👁</span>
                                                {photoDetails.viewCount !== undefined ? photoDetails.viewCount : 0}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Likes:</th>
                                            <td className="stats-value">
                                                <span className="stats-icon">❤</span>
                                                {photoDetails.likeCount !== undefined ? photoDetails.likeCount : 0}
                                            </td>
                                        </tr>
                                    </>
                                )}
                            </tbody>
                        </table>

                        <div className="album-selector-section">
                            <h3>Album</h3>
                            <div className="album-selector-controls">
                                <select
                                    value={selectedAlbumId || ''}
                                    onChange={handleAlbumChange}
                                    disabled={saving || albums.length === 0}
                                    className="album-selector"
                                >
                                    {albums.map(album => (
                                        <option key={album.id} value={album.id}>
                                            {album.name} {album.is_default ? '(Default)' : ''}
                                        </option>
                                    ))}
                                </select>
                                {saving && <span className="saving-indicator">Saving...</span>}
                                {saveMessage && (
                                    <span className={`save-message ${saveMessage.includes('Failed') ? 'error' : 'success'}`}>
                                        {saveMessage}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="title-section">
                            <h3>Photo Title</h3>
                            <div className="title-controls">
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    onBlur={handleTitleBlur}
                                    placeholder="Enter a title for this photo (optional)"
                                    disabled={titleSaving}
                                    className="title-input"
                                    maxLength={500}
                                />
                                {titleSaving && <span className="saving-indicator">Saving...</span>}
                            </div>
                            {title && (
                                <p className="title-help-text">This title will be displayed instead of the filename</p>
                            )}
                        </div>

                        <div className="published-section">
                            <h3>Published Status</h3>
                            <div className="published-controls">
                                <label className="published-toggle">
                                    <input
                                        type="checkbox"
                                        checked={isPublished}
                                        onChange={handlePublishedChange}
                                        disabled={publishSaving}
                                    />
                                    <span className="published-label">
                                        {isPublished ? 'Published' : 'Not Published'}
                                    </span>
                                    {publishSaving && <span className="saving-indicator">Saving...</span>}
                                </label>
                                {isPublished && (
                                    <span className="published-badge">✓ Public</span>
                                )}
                            </div>
                        </div>

                        <div className="cover-section">
                            <h3>Album Cover</h3>
                            <div className="cover-controls">
                                <label className="cover-toggle">
                                    <input
                                        type="checkbox"
                                        checked={isCover}
                                        onChange={handleCoverChange}
                                        disabled={coverSaving}
                                    />
                                    <span className="cover-label">
                                        {isCover ? 'Album Cover Photo' : 'Not Album Cover'}
                                    </span>
                                    {coverSaving && <span className="saving-indicator">Saving...</span>}
                                </label>
                                {isCover && (
                                    <span className="cover-badge">★ Cover</span>
                                )}
                            </div>
                            <p className="cover-help-text">
                                Setting this as the album cover will remove cover status from any other photo in this album.
                            </p>
                        </div>

                        <div className="delete-section">
                            <h3>Danger Zone</h3>
                            {!showDeleteConfirm ? (
                                <button
                                    className="delete-btn"
                                    onClick={() => setShowDeleteConfirm(true)}
                                >
                                    Delete Photo
                                </button>
                            ) : (
                                <div className="delete-confirm">
                                    <p className="delete-warning">
                                        Are you sure you want to delete this photo? This action cannot be undone.
                                    </p>
                                    <div className="delete-confirm-buttons">
                                        <button
                                            className="delete-confirm-btn"
                                            onClick={handleDelete}
                                            disabled={deleting}
                                        >
                                            {deleting ? 'Deleting...' : 'Yes, Delete'}
                                        </button>
                                        <button
                                            className="delete-cancel-btn"
                                            onClick={() => setShowDeleteConfirm(false)}
                                            disabled={deleting}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {Object.keys(categorizedMetadata).length === 0 ? (
                            <p className="no-metadata">No EXIF metadata available for this image.</p>
                        ) : (
                            <>
                                {Object.entries(categorizedMetadata).map(([category, items]) => (
                                    <div key={category}>
                                        <h3>{category}</h3>
                                        <table className="metadata-table">
                                            <tbody>
                                                {items.map(({ key, label, value }) => (
                                                    <tr key={key}>
                                                        <th>{label}:</th>
                                                        <td className={typeof value === 'object' ? 'json-value' : ''}>
                                                            {typeof value === 'object' ? (
                                                                <pre>{value}</pre>
                                                            ) : (
                                                                value
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PhotoViewer;
